/**
 * Policies list column metadata (OR-1664, design OR-1537).
 *
 * Single source of truth for which columns exist, their label, width,
 * sort field and Customize section. Rendering of each cell lives in
 * `policies-table.tsx` (keyed by column `id`), so this stays a pure-data
 * module — easy to feed the Customize drawer and the saved layout.
 *
 * Natural order is the table order in the approved MVP design. The
 * Customize sections (Policy / Premium & Exposure / Brokerage) group the
 * same columns differently, which is why `customizeGroup` is set per
 * column instead of the table being banded: the design shows no group
 * band above this table.
 *
 * Every column is on by default — the MVP table shows all nine. Column
 * ids double as the backend sort field where the column is sortable.
 */
export type PolicyColumnGroup = "policy" | "premium" | "brokerage";

export const GROUP_LABELS: Record<PolicyColumnGroup, string> = {
    policy: "Policy",
    premium: "Premium & Exposure",
    brokerage: "Brokerage",
};

export const GROUP_ORDER: readonly PolicyColumnGroup[] = ["policy", "premium", "brokerage"];

export interface PolicyColumnDef {
    /** Stable id — also the sort field sent to the API when `sortable`. */
    id: string;
    label: string;
    group: PolicyColumnGroup;
    width: number;
    /** Sortable header (chevron). Default false. */
    sortable?: boolean;
    /** Shown before the user customizes anything. */
    defaultVisible?: boolean;
}

export const POLICY_COLUMNS: PolicyColumnDef[] = [
    { id: "priority", label: "Priority", group: "policy", width: 170, defaultVisible: true },
    { id: "policy_number", label: "Policy number", group: "policy", width: 190, sortable: true, defaultVisible: true },
    { id: "named_insured", label: "Name Insured", group: "policy", width: 200, sortable: true, defaultVisible: true },
    { id: "status", label: "Status", group: "policy", width: 150, defaultVisible: true },
    { id: "total_premium", label: "Total Premium", group: "premium", width: 120, sortable: true, defaultVisible: true },
    { id: "effective_date", label: "Inception Date", group: "policy", width: 120, sortable: true, defaultVisible: true },
    { id: "expiration_date", label: "Expiration date", group: "policy", width: 120, sortable: true, defaultVisible: true },
    { id: "facility_count", label: "Facility Count", group: "premium", width: 110, sortable: true, defaultVisible: true },
    { id: "brokerage_name", label: "Brokerage", group: "brokerage", width: 160, sortable: true, defaultVisible: true },
];

/** Default visible column ids (the MVP table). */
export function defaultVisibleIds(): string[] {
    return POLICY_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.id);
}

/** Every column id in natural (design) order. Seeds drag-to-reorder. */
export function allColumnIds(): string[] {
    return POLICY_COLUMNS.map((c) => c.id);
}
